import React, { useState, useEffect } from 'react';
import { useSOCCache } from '../../hooks/useSOCCache';
import MITREMatrix   from './MITREMatrix';
import PlaybookPanel from './PlaybookPanel';
import SLATimer, { SLABadge } from './SLATimer';

const SEV_COLOR = { HIGH:'#ff3b4e', MEDIUM:'#f59e0b', LOW:'#10b981', CRITICAL:'#ff0040', UNKNOWN:'#6b7280' };
const SEV_BG    = { HIGH:'#ff3b4e18', MEDIUM:'#f59e0b18', LOW:'#10b98118', CRITICAL:'#ff004018', UNKNOWN:'#6b728018' };
const ATTACK_COLORS = {'APT-Reconnaissance':'#a78bfa','APT-Lateral-Movement':'#38bdf8','APT-Persistence':'#f472b6','APT-Privilege-Escalation':'#fb923c','APT-Defense-Evasion':'#facc15','APT-C2-Communication':'#4ade80','APT-Data-Exfiltration':'#f87171','APT-Living-Off-The-Land':'#e879f9'};
const MITRE_MAP = {'APT-Reconnaissance':{id:'TA0043',name:'Reconnaissance'},'APT-Lateral-Movement':{id:'TA0008',name:'Lateral Movement'},'APT-Persistence':{id:'TA0003',name:'Persistence'},'APT-Privilege-Escalation':{id:'TA0004',name:'Privilege Escalation'},'APT-Defense-Evasion':{id:'TA0005',name:'Defense Evasion'},'APT-C2-Communication':{id:'TA0011',name:'Command & Control'},'APT-Data-Exfiltration':{id:'TA0010',name:'Exfiltration'},'APT-Living-Off-The-Land':{id:'TA0002',name:'Execution'}};
const KILL_CHAIN = ['APT-Reconnaissance','APT-Living-Off-The-Land','APT-Privilege-Escalation','APT-Persistence','APT-Lateral-Movement','APT-Defense-Evasion','APT-C2-Communication','APT-Data-Exfiltration'];
const panel = { background:'#141720', border:'1px solid #ffffff14', borderRadius:8, padding:'14px 16px' };
const mono  = { fontFamily:'JetBrains Mono, monospace' };
const PAGE_SIZE = 50;

function getConf(inc) {
  const c = inc.confidence;
  if (typeof c === 'number' && c > 0) return c;
  const r = inc.avg_risk_score;
  if (typeof r === 'number' && r > 0) return r;
  return 0;
}

function ConfBar({ value }) {
  const pct = Math.min(Math.round(value * 100), 100);
  const col = pct >= 80 ? '#ff3b4e' : pct >= 60 ? '#f59e0b' : '#10b981';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:5, background:'#ffffff10', borderRadius:3, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:col, borderRadius:3 }} />
      </div>
      <span style={{ fontSize:11, color:col, ...mono, minWidth:32, textAlign:'right' }}>{pct}%</span>
    </div>
  );
}

function Badge({ sev }) {
  const s = (sev||'UNKNOWN').toUpperCase();
  const c = SEV_COLOR[s]||SEV_COLOR.UNKNOWN;
  return <span style={{ fontSize:10, ...mono, fontWeight:700, color:c, background:SEV_BG[s]||SEV_BG.UNKNOWN, border:`1px solid ${c}44`, borderRadius:4, padding:'2px 7px' }}>{s}</span>;
}

function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const getPages = () => {
    const pages = []; const delta = 2; let last = 0;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
        if (last && i - last > 1) pages.push('...');
        pages.push(i); last = i;
      }
    }
    return pages;
  };
  const btn = (content, target, disabled, active) => (
    <button key={String(content)+String(target)} onClick={() => !disabled && onPage(target)} disabled={disabled}
      style={{ background:active?'#3b82f6':'#141720', border:`1px solid ${active?'#3b82f6':'#ffffff18'}`, borderRadius:5, padding:'4px 9px', cursor:disabled?'default':'pointer', fontSize:11, ...mono, color:disabled?'#374151':active?'#fff':'#9ca3af', opacity:disabled?0.5:1, minWidth:32 }}>
      {content}
    </button>
  );
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4, paddingTop:10, borderTop:'1px solid #ffffff0a', flexWrap:'wrap' }}>
      {btn('«',1,page===1,false)}{btn('‹',page-1,page===1,false)}
      {getPages().map((p,i) => p==='...'
        ? <span key={`e${i}`} style={{ color:'#374151', fontSize:11, padding:'0 4px' }}>…</span>
        : btn(p,p,false,p===page)
      )}
      {btn('›',page+1,page===totalPages,false)}{btn('»',totalPages,page===totalPages,false)}
    </div>
  );
}

function Timeline({ inc }) {
  const [showMitre, setShowMitre] = useState(false);

  if (!inc) return (
    <div style={{ ...panel, flex:1, display:'flex', flexDirection:'column' }}>
      <div style={{ fontSize:11, color:'#6b7280', ...mono, marginBottom:8 }}>ATTACK TIMELINE</div>
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ textAlign:'center', color:'#374151' }}>
          <div style={{ fontSize:28, marginBottom:8 }}>⬡</div>
          <div style={{ fontSize:12, ...mono }}>Select an incident</div>
        </div>
      </div>
    </div>
  );

  const conf   = getConf(inc);
  const ips    = inc.ips || [];
  const types  = inc.attack_types || [];
  const sorted = [...types].sort((a,b) => {
    const ai=KILL_CHAIN.indexOf(a); const bi=KILL_CHAIN.indexOf(b);
    return (ai===-1?99:ai)-(bi===-1?99:bi);
  });

  return (
    <div style={{ ...panel, flex:1, display:'flex', flexDirection:'column', overflow:'auto' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div style={{ fontSize:11, color:'#6b7280', ...mono }}>ATTACK TIMELINE — {inc.id}</div>
        <Badge sev={inc.severity} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:14 }}>
        {[['EVENTS',inc.event_count||0],['SOURCE IPs',ips.length],['CONFIDENCE',`${Math.min(Math.round(conf*100),100)}%`]].map(([l,v])=>(
          <div key={l} style={{ background:'#0d1018', borderRadius:6, padding:'8px 10px', border:'1px solid #ffffff0a' }}>
            <div style={{ fontSize:9, color:'#4b5563', ...mono, marginBottom:4 }}>{l}</div>
            <div style={{ fontSize:18, fontWeight:700, ...mono, color:'#e8eaf0' }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Kill chain */}
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:10, color:'#4b5563', ...mono, marginBottom:10 }}>KILL CHAIN PROGRESSION</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
          {sorted.map((type,idx)=>{
            const col=ATTACK_COLORS[type]||'#6b7280';
            const m=MITRE_MAP[type];
            return (
              <div key={type} style={{ background:`${col}12`, border:`1px solid ${col}44`, borderRadius:6, padding:'8px 10px', minWidth:140, flex:'1 1 140px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:col }} />
                  <span style={{ fontSize:10, ...mono, color:col, fontWeight:600 }}>STAGE {idx+1}</span>
                </div>
                <div style={{ fontSize:11, color:'#e8eaf0', ...mono, marginBottom:4 }}>{type.replace('APT-','')}</div>
                {m && <span style={{ fontSize:9, color:'#6366f1', ...mono }}>{m.id} · {m.name}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* MITRE matrix toggle */}
      <button onClick={() => setShowMitre(v => !v)}
        style={{ width:'100%', padding:'5px 10px', marginBottom:10, borderRadius:4, fontSize:10, cursor:'pointer', border:'1px solid #6366f133', background:'#6366f10a', color:'#818cf8', ...mono, textAlign:'left' }}>
        {showMitre ? '▲ Hide MITRE ATT&CK Matrix' : '▼ Show Full MITRE ATT&CK Matrix'}
      </button>
      {showMitre && <MITREMatrix attackTypes={inc.attack_types || []} />}

      {/* Confidence */}
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:10, color:'#4b5563', ...mono, marginBottom:8 }}>OVERALL CONFIDENCE</div>
        <ConfBar value={conf} />
      </div>

      {/* IPs */}
      {ips.length > 0 && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:10, color:'#4b5563', ...mono, marginBottom:8 }}>INVOLVED SOURCE IPs</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {ips.slice(0,12).map(ip=>(
              <span key={ip} style={{ fontSize:10, ...mono, color:'#38bdf8', background:'#38bdf810', border:'1px solid #38bdf820', borderRadius:4, padding:'3px 7px' }}>{ip}</span>
            ))}
          </div>
        </div>
      )}

      {/* Playbooks */}
      <PlaybookPanel attackTypes={inc.attack_types || []} severity={inc.severity} incidentId={inc.id} onResolved={() => window.dispatchEvent(new CustomEvent("aegis:incident-updated"))} />

      {/* SLA Timer */}
      <SLATimer incidentId={inc.id} createdAt={inc.created_at || inc.timestamp} />
    </div>
  );
}

function Card({ inc, selected, onClick }) {
  const conf = getConf(inc);
  const sev  = (inc.severity||'UNKNOWN').toUpperCase();
  const col  = SEV_COLOR[sev]||SEV_COLOR.UNKNOWN;
  const ips  = inc.ips || [];
  return (
    <div onClick={onClick} style={{ background:selected?'#1a1e2a':'#141720', border:selected?`1px solid ${col}66`:'1px solid #ffffff10', borderLeft:selected?`3px solid ${col}`:'3px solid transparent', borderRadius:8, padding:'12px 14px', cursor:'pointer', marginBottom:6, transition:'all 0.15s' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
        <span style={{ fontSize:12, fontWeight:700, ...mono, color:'#e8eaf0' }}>{inc.id}</span>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <SLABadge createdAt={inc.created_at || inc.timestamp} />
          <Badge sev={sev} />
        </div>
      </div>
      <div style={{ fontSize:10, color:'#6b7280', ...mono, marginBottom:8, lineHeight:1.5 }}>
        {(inc.attack_types||[]).slice(0,4).join(', ')}{(inc.attack_types||[]).length>4?` +${inc.attack_types.length-4}`:''}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ fontSize:10, color:'#4b5563', ...mono }}>{inc.event_count||0} events · {ips.length} IPs</span>
        <div style={{ flex:1 }}><ConfBar value={conf} /></div>
      </div>
    </div>
  );
}

export default function Incidents() {
  // ── Cache hook — survives sign-out/sign-in ────────────────────────────────
  const { incidents, loading } = useSOCCache();

  const [selected, setSelected] = useState(null);
  const [filter,   setFilter]   = useState('ALL');
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);

  useEffect(() => { setPage(1); }, [filter, search]);

  const counts = { ALL: incidents.length, CRITICAL:0, HIGH:0, MEDIUM:0, LOW:0 };
  incidents.forEach(i => { const s=(i.severity||'').toUpperCase(); if(counts[s]!==undefined) counts[s]++; });

  const filtered = incidents.filter(i => {
    const s  = (i.severity||'').toUpperCase();
    const ms = filter==='ALL' || s===filter;
    const mq = !search ||
      i.id?.toLowerCase().includes(search.toLowerCase()) ||
      (i.attack_types||[]).some(t=>t.toLowerCase().includes(search.toLowerCase())) ||
      (i.ips||[]).some(ip=>ip.includes(search));
    return ms && mq;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageSlice  = filtered.slice((safePage-1)*PAGE_SIZE, safePage*PAGE_SIZE);

  // Show stale cache instantly — only show spinner if truly empty
  if (loading && incidents.length === 0) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300 }}>
        <div style={{ fontSize:12, color:'#374151', ...mono }}>Loading incidents...</div>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div style={{ fontSize:11, color:'#9ca3af', ...mono }}>
          ACTIVE INCIDENTS ({filtered.length.toLocaleString()}{filter!=='ALL'||search?` of ${incidents.length.toLocaleString()}`:''})
        </div>
        <div style={{ display:'flex', gap:6, marginLeft:'auto' }}>
          {['ALL','CRITICAL','HIGH','MEDIUM','LOW'].map(f=>(
            <button key={f} onClick={()=>setFilter(f)}
              style={{ background:filter===f?(SEV_COLOR[f]||'#3b82f6')+'22':'#141720', border:`1px solid ${filter===f?(SEV_COLOR[f]||'#3b82f6')+'66':'#ffffff14'}`, borderRadius:5, padding:'4px 10px', cursor:'pointer', fontSize:10, ...mono, color:filter===f?(SEV_COLOR[f]||'#3b82f6'):'#6b7280' }}>
              {f}{counts[f]>0&&<span style={{opacity:.7}}> ({counts[f].toLocaleString()})</span>}
            </button>
          ))}
        </div>
        <input placeholder="Search ID / IP / type..." value={search} onChange={e=>setSearch(e.target.value)}
          style={{ background:'#0d1018', border:'1px solid #ffffff14', borderRadius:6, padding:'5px 10px', fontSize:11, ...mono, color:'#e8eaf0', outline:'none', width:200 }} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'380px 1fr', gap:12 }}>
        <div style={{ ...panel, overflow:'hidden', display:'flex', flexDirection:'column', maxHeight:'80vh' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ fontSize:11, color:'#6b7280', ...mono }}>INCIDENT LIST</div>
            {totalPages>1 && <div style={{ fontSize:10, color:'#4b5563', ...mono }}>Page {safePage} / {totalPages}</div>}
          </div>
          <div style={{ flex:1, overflowY:'auto', marginBottom:totalPages>1?10:0 }}>
            {pageSlice.length===0
              ? <div style={{ textAlign:'center', color:'#374151', padding:40, ...mono, fontSize:12 }}>No incidents found</div>
              : pageSlice.map(inc=><Card key={inc.id} inc={inc} selected={selected?.id===inc.id} onClick={()=>setSelected(inc)} />)
            }
          </div>
          <Pagination page={safePage} totalPages={totalPages} onPage={p=>{ setPage(p); setSelected(null); }} />
        </div>
        <Timeline inc={selected} />
      </div>
    </div>
  );
}
